import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MilitaryExchange,
  OrderDetailsDataSessionObject,
  OrderDetailsSessionDataV2,
} from '../models/returns';
import { isSsr } from '../utils/general';
import { SSR_SESSION_STORAGE_KEY } from '../constants/general';
import { getFromStorage } from '../utils/storage-helper';
import { ITender } from '../models/common';
import { setReturnSession } from '../stores/returnStore';

export interface IReturnSession {
  tenders: ITender[];
  orderDetailsV2?: OrderDetailsSessionDataV2;
  orderId?: string;
  militaryExchange: MilitaryExchange;
  isMilitaryExchangeGuest: boolean;
  orderBarcode?: string;
  receiptNumber?: string;
  orderDetailsPath: string;
  ptcNickname?: string;
  isPTCOrder?: boolean;
  isApoFpo?: boolean;
}

export const useReturnSession = (): IReturnSession => {
  const navigate = useNavigate();

  const [orderDetailsDataResponse, setOrderDetailsDataResponse] = useState<
    OrderDetailsDataSessionObject | undefined
  >();

  const fetchOrderDetailsData = async () => {
    if (!isSsr && window.sessionStorage) {
      const windowData = await getFromStorage(SSR_SESSION_STORAGE_KEY);
      if (windowData) {
        const parsedData = JSON.parse(
          windowData,
        )[0] as OrderDetailsDataSessionObject;
        // order id is required, redirecting
        if (!parsedData?.orderId) {
          navigate('/myaccount/purchase-history', { replace: true });
          return null;
        }
        setOrderDetailsDataResponse(parsedData);

        // Set the Zustand store directly from the fetch callback instead of
        // relying on a secondary useEffect([orderDetailsDataResponse]).
        // This eliminates an async hop where the chain could break if the
        // component tree unmounts/remounts during hydration.
        const {
          tenders: fetchedTenders = [],
          orderDetailsDataV2: fetchedOrderDetails,
          orderId: fetchedOrderId,
          isGuest: fetchedIsGuest = false,
          militaryExchange: fetchedMilitaryExchange = '' as MilitaryExchange,
          orderBarcode: fetchedOrderBarcode = '',
          receiptNumber: fetchedReceiptNumber = '',
          ptcNickname: fetchedPtcNickname,
          isPTCOrder: fetchedIsPTCOrder,
          isApoFpo: fetchedIsApoFpo,
        } = parsedData;
        const fetchedOrderDetailsPath =
          parsedData.orderDetailsPath ||
          '/myaccount/order-details?orderNumber=' + fetchedOrderId;
        const fetchedIsMilitaryExchangeGuest =
          Boolean(fetchedMilitaryExchange) && fetchedIsGuest;

        if (fetchedOrderDetails && fetchedOrderId) {
          setReturnSession({
            orderDetailsV2: fetchedOrderDetails,
            tenders: fetchedTenders,
            orderId: fetchedOrderId,
            isMilitaryExchangeGuest: fetchedIsMilitaryExchangeGuest,
            militaryExchange: fetchedMilitaryExchange,
            orderBarcode: fetchedOrderBarcode,
            receiptNumber: fetchedReceiptNumber,
            orderDetailsPath: fetchedOrderDetailsPath,
            ptcNickname: fetchedPtcNickname,
            isPTCOrder: fetchedIsPTCOrder,
            isApoFpo: fetchedIsApoFpo,
          });
        }
      } else {
        // Redirecting for no Session
        navigate('/myaccount/purchase-history', { replace: true });
      }
    }
    return null;
  };

  const {
    tenders = [],
    orderDetailsDataV2,
    orderId,
    isGuest = false,
    militaryExchange = '',
    orderBarcode = '',
    receiptNumber = '',
    ptcNickname,
    isPTCOrder,
    isApoFpo,
  } = orderDetailsDataResponse ?? {};
  const orderDetailsPath =
    orderDetailsDataResponse?.orderDetailsPath ||
    '/myaccount/order-details?orderNumber=' + orderId;
  const isMilitaryExchangeGuest = Boolean(militaryExchange) && isGuest;

  // Fetches order details data on render
  useEffect(() => {
    fetchOrderDetailsData();
  }, []);

  return {
    orderDetailsV2: orderDetailsDataV2,
    tenders,
    orderId,
    isMilitaryExchangeGuest,
    militaryExchange,
    orderBarcode,
    receiptNumber,
    orderDetailsPath,
    ptcNickname,
    isPTCOrder,
    isApoFpo,
  };
};

export default useReturnSession;
